'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { Sparkles, Play, ArrowRight, Zap, Globe, Shield } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const rotatingWords = ['Designs', 'Campaigns', 'Videos', 'Menus', 'Posters', 'Social Content', 'Brand Kits', 'QR Codes'];

const trustBadges = [
  { icon: Zap, label: 'AI-Powered Creation' },
  { icon: Globe, label: '12+ Languages' },
  { icon: Shield, label: 'Designed with privacy in mind' },
];

// Realistic design output previews
const mockupItems = [
  {
    label: 'Restaurant Menu',
    color: 'from-amber-500/20 to-orange-500/10',
    preview: (
      <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" aria-label="Restaurant menu design example">
        <rect width="80" height="80" rx="6" fill="#1a1008"/>
        <rect x="6" y="6" width="68" height="68" rx="4" fill="#2a1a0a"/>
        <rect x="10" y="10" width="60" height="8" rx="2" fill="#D4AF37" opacity="0.7"/>
        <rect x="10" y="22" width="26" height="3" rx="1" fill="#fff" opacity="0.6"/>
        <rect x="10" y="27" width="18" height="2" rx="1" fill="#fff" opacity="0.3"/>
        <rect x="42" y="22" width="26" height="3" rx="1" fill="#fff" opacity="0.6"/>
        <rect x="42" y="27" width="18" height="2" rx="1" fill="#fff" opacity="0.3"/>
        <rect x="10" y="34" width="60" height="1" rx="0.5" fill="#D4AF37" opacity="0.3"/>
        <rect x="10" y="38" width="22" height="2" rx="1" fill="#D4AF37" opacity="0.5"/>
        <rect x="10" y="44" width="60" height="28" rx="3" fill="#1a1008"/>
        <rect x="14" y="48" width="22" height="12" rx="2" fill="#D4AF37" opacity="0.12"/>
        <rect x="40" y="48" width="22" height="12" rx="2" fill="#D4AF37" opacity="0.12"/>
        <rect x="16" y="50" width="14" height="2" rx="1" fill="#fff" opacity="0.5"/>
        <rect x="16" y="54" width="10" height="1.5" rx="0.75" fill="#D4AF37" opacity="0.6"/>
        <rect x="42" y="50" width="14" height="2" rx="1" fill="#fff" opacity="0.5"/>
        <rect x="42" y="54" width="10" height="1.5" rx="0.75" fill="#D4AF37" opacity="0.6"/>
        <rect x="14" y="64" width="52" height="4" rx="2" fill="#D4AF37" opacity="0.2"/>
        <rect x="22" y="65.5" width="36" height="1" rx="0.5" fill="#D4AF37" opacity="0.5"/>
      </svg>
    ),
  },
  {
    label: 'Nail Studio Post',
    color: 'from-pink-500/20 to-purple-500/10',
    preview: (
      <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" aria-label="Nail studio Instagram post example">
        <rect width="80" height="80" rx="6" fill="#1a0a14"/>
        <rect x="6" y="6" width="68" height="68" rx="4" fill="#2a0f1e"/>
        <circle cx="40" cy="30" r="16" fill="#F472B6" opacity="0.2"/>
        <circle cx="40" cy="30" r="11" fill="#F472B6" opacity="0.35"/>
        <circle cx="40" cy="30" r="7" fill="#F472B6" opacity="0.6"/>
        <rect x="14" y="52" width="52" height="3" rx="1.5" fill="#F472B6" opacity="0.6"/>
        <rect x="20" y="58" width="40" height="2" rx="1" fill="#fff" opacity="0.4"/>
        <rect x="26" y="63" width="28" height="7" rx="3.5" fill="#D4AF37" opacity="0.7"/>
        <rect x="30" y="65.5" width="20" height="2" rx="1" fill="#fff" opacity="0.9"/>
      </svg>
    ),
  },
  {
    label: 'Real Estate Listing',
    color: 'from-blue-500/20 to-cyan-500/10',
    preview: (
      <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" aria-label="Real estate property listing example">
        <rect width="80" height="80" rx="6" fill="#080e1a"/>
        <rect x="6" y="6" width="68" height="42" rx="4" fill="#0f1e2a"/>
        <rect x="10" y="10" width="60" height="34" rx="3" fill="#1a2a3a"/>
        <polygon points="40,14 56,24 56,44 24,44 24,24" fill="#2a3a4a"/>
        <rect x="34" y="30" width="12" height="14" rx="1" fill="#3a4a5a"/>
        <rect x="10" y="52" width="36" height="3" rx="1" fill="#fff" opacity="0.7"/>
        <rect x="10" y="58" width="24" height="2" rx="1" fill="#fff" opacity="0.4"/>
        <rect x="50" y="50" width="20" height="10" rx="2" fill="#D4AF37" opacity="0.8"/>
        <rect x="52" y="53" width="16" height="2" rx="1" fill="#fff" opacity="0.9"/>
        <rect x="10" y="64" width="60" height="1" rx="0.5" fill="#D4AF37" opacity="0.3"/>
        <rect x="10" y="68" width="18" height="2" rx="1" fill="#D4AF37" opacity="0.5"/>
      </svg>
    ),
  },
  {
    label: 'YouTube Thumbnail',
    color: 'from-red-500/20 to-orange-500/10',
    preview: (
      <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" aria-label="YouTube thumbnail design example">
        <rect width="80" height="80" rx="6" fill="#0a0808"/>
        <rect x="6" y="12" width="68" height="38" rx="4" fill="#1a0f0f"/>
        <circle cx="40" cy="31" r="13" fill="#FF0000" opacity="0.8"/>
        <polygon points="37,26 37,36 48,31" fill="white"/>
        <rect x="6" y="54" width="68" height="20" rx="4" fill="#1a0f0f"/>
        <rect x="10" y="58" width="44" height="3" rx="1.5" fill="#fff" opacity="0.8"/>
        <rect x="10" y="64" width="30" height="2" rx="1" fill="#fff" opacity="0.4"/>
        <rect x="58" y="57" width="12" height="7" rx="2" fill="#FF0000" opacity="0.7"/>
      </svg>
    ),
  },
  {
    label: 'Wedding Invitation',
    color: 'from-purple-500/20 to-pink-500/10',
    preview: (
      <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" aria-label="Wedding invitation design example">
        <rect width="80" height="80" rx="6" fill="#0f0a14"/>
        <rect x="6" y="6" width="68" height="68" rx="4" fill="#1a1020"/>
        <rect x="10" y="10" width="60" height="60" rx="3" stroke="#D4AF37" strokeWidth="0.5" strokeDasharray="2 2" fill="none"/>
        <path d="M32 28 Q40 20 48 28 Q40 36 32 28Z" fill="#D4AF37" opacity="0.5"/>
        <rect x="22" y="40" width="36" height="3" rx="1.5" fill="#fff" opacity="0.8"/>
        <rect x="26" y="46" width="28" height="2" rx="1" fill="#D4AF37" opacity="0.6"/>
        <rect x="28" y="52" width="24" height="2" rx="1" fill="#fff" opacity="0.4"/>
        <rect x="30" y="58" width="20" height="6" rx="3" fill="#D4AF37" opacity="0.3"/>
      </svg>
    ),
  },
];

export default function HeroSection() {
  const [wordIndex, setWordIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) return;
    intervalRef.current = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setWordIndex((i) => (i + 1) % rotatingWords.length);
        setFade(true);
      }, 250);
    }, 2200);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [prefersReducedMotion]);

  const ctaHref = user ? '/create' : '/auth/sign-up';

  return (
    <section className="relative min-h-screen flex items-center justify-center hero-grid-bg overflow-hidden pt-16">
      <div className="blob-gold absolute top-1/4 left-1/4 w-96 h-96 opacity-60" />
      <div className="blob-purple absolute bottom-1/4 right-1/4 w-80 h-80 opacity-40" />
      <div className="blob-gold absolute top-3/4 right-1/3 w-64 h-64 opacity-30" />

      <div className="relative z-10 max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 text-center w-full">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6 animate-fade-in">
          <Sparkles size={14} className="animate-pulse-gold" />
          AI-Powered Creative Platform
          <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-foreground mb-4 animate-slide-up leading-tight">
          Create Every Design Your
          <br className="hidden sm:block" />
          <span className="text-gradient-gold"> Business or Life Needs</span>
        </h1>

        {prefersReducedMotion ? (
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="text-lg lg:text-xl font-medium text-slate-300">
              AI creates your <span className="font-bold text-primary">Designs, Campaigns, Videos & More</span>
            </span>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="text-lg lg:text-xl font-medium text-slate-300">AI creates your</span>
            <span
              className={`text-lg lg:text-xl font-bold text-primary transition-opacity duration-300 min-w-[140px] sm:min-w-[160px] text-left ${fade ? 'opacity-100' : 'opacity-0'}`}
            >
              {rotatingWords[wordIndex]}
            </span>
          </div>
        )}

        <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto mb-8 leading-relaxed px-2">
          From one simple request to complete designs, videos, campaigns, QR codes, and print-ready files — created with AI and fully editable.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-10">
          <Link
            href={ctaHref}
            className="btn-primary flex items-center gap-2.5 px-8 py-4 rounded-2xl text-base font-semibold gold-glow w-full sm:w-auto justify-center"
          >
            <Sparkles size={18} />
            Start Creating Free
            <ArrowRight size={16} />
          </Link>
          <Link
            href="/examples"
            className="btn-secondary flex items-center gap-2.5 px-8 py-4 rounded-2xl text-base font-medium w-full sm:w-auto justify-center"
          >
            <Play size={16} className="fill-current" />
            See a Real Example
          </Link>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mb-10">
          {trustBadges?.map((badge) => (
            <div key={`trust-${badge?.label}`} className="flex items-center gap-2 text-sm text-slate-400">
              <badge.icon size={15} className="text-primary" />
              {badge?.label}
            </div>
          ))}
        </div>

        <div className="relative overflow-hidden rounded-3xl border border-border bg-card/50 backdrop-blur-sm p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/80 z-10 pointer-events-none rounded-3xl" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {mockupItems?.map((item, i) => (
              <div
                key={`mockup-${item?.label}`}
                className={`bg-gradient-to-br ${item?.color} border border-white/5 rounded-2xl overflow-hidden flex flex-col items-center justify-center aspect-square text-center`}
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="w-full h-full">{item?.preview}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-400 relative z-20">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            Example outputs created with DESIGNHUB AI
          </div>
        </div>
      </div>
    </section>
  );
}